import { toast } from "sonner";
import { clearCache } from "@/queryClient";
import { useAuthStore } from "@/shared/state/authstore";
import {
    employmentRevokedMessage,
    employmentSuspendedMessage,
    isEmploymentRevoked,
    isEmploymentSuspended,
    revokedAccountIdOf,
} from "./employmentRevoked";

/**
 * Explains an `EMPLOYMENT_REVOKED` refusal and reports whether it was one, so the caller can skip its
 * generic "you do not have permission" message.
 *
 * Only that organisation is let go of. The refresh token and the other memberships are left alone,
 * because someone may work for several firms and losing one must not take the others with it. If
 * the revoked organisation was the one on screen, the store drops it as the selected account and the
 * layout falls back to the "choose an organisation" screen, which says what happened. The cache goes
 * with it: it holds that organisation's data, and the user no longer has any claim to it.
 *
 * Idempotent on purpose. A page fires a dozen requests at once and every one of them is refused, so
 * the toast carries a stable id (one message, not twelve) and a repeat finds the account already
 * marked.
 */
export function explainEmploymentRevoked(error: unknown, options: { silent?: boolean } = {}): boolean {
    const suspended = isEmploymentSuspended(error);
    if (!suspended && !isEmploymentRevoked(error)) return false;

    const store = useAuthStore.getState();
    const accountId = revokedAccountIdOf(error);
    const accountName = accountId === null
        ? null
        : store.availableAccounts.find((a) => a.accountId === accountId)?.accountName ?? null;

    if (accountId !== null && store.markAccountRevoked(accountId, suspended)) {
        void clearCache();
    }

    if (!options.silent) {
        const message = suspended ? employmentSuspendedMessage(accountName) : employmentRevokedMessage(accountName);
        toast.error(message, { id: `employment-revoked-${accountId ?? "unknown"}` });
    }
    return true;
}
